<template>
  <div class="uploaded-file-card" :class="{ 'is-uploading': !isUploadSuccess }" :style="cardStyle">
    <img
      alt=""
      class="uploaded-file-card__icon"
      :src="iconSrc"
      @error="failedImageSrc = props.file?.attachIndex"
    />
    <div class="uploaded-file-card__info">
      <el-tooltip :content="fileName" :disabled="!!isMobile()" effect="dark" placement="top">
        <div class="uploaded-file-card__name">{{ fileName }}</div>
      </el-tooltip>
      <div v-if="isUploadSuccess" class="uploaded-file-card__meta">
        <slot
          :file="props.file"
          :file-ext="fileExt"
          :file-meta="fileMeta"
          :file-type-text="fileTypeText"
          name="meta"
        >
          {{ fileMeta }}
        </slot>
      </div>
      <div v-else class="uploaded-file-card__uploading">
        <i class="icon-loader-2-line t-iconfont uploaded-file-card__loader" />
        <span>上传中...</span>
      </div>
    </div>

    <button
      v-if="isUploadSuccess && hasDeleteListener"
      aria-label="删除文件"
      class="uploaded-file-card__delete"
      type="button"
      @click.stop="emits('delete', props.file)"
    >
      <i class="icon-close-line t-iconfont" />
    </button>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue'
import { isMobile } from '@/utils'
import fileOtherIcon from '@/assets/file-icon/other.png'
import fileTextIcon from '@/assets/file-icon/text.png'
import filePdfIcon from '@/assets/file-icon/pdf.png'
import fileMdIcon from '@/assets/file-icon/md.png'
import fileWordIcon from '@/assets/file-icon/word.png'
import fileExcelIcon from '@/assets/file-icon/excel.png'
import fileImageIcon from '@/assets/file-icon/image.png'
import filePptIcon from '@/assets/file-icon/ic_file_ppt.png'

const FILE_ICON_BY_EXTENSION = {
  pdf: filePdfIcon,
  doc: fileWordIcon,
  docx: fileWordIcon,
  xls: fileExcelIcon,
  xlsx: fileExcelIcon,
  csv: fileExcelIcon,
  ppt: filePptIcon,
  pptx: filePptIcon,
  md: fileMdIcon,
  markdown: fileMdIcon,
  txt: fileTextIcon,
  text: fileTextIcon,
  log: fileTextIcon,
  json: fileTextIcon,
  jsonl: fileTextIcon,
  yaml: fileTextIcon,
  yml: fileTextIcon,
  png: fileImageIcon,
  jpg: fileImageIcon,
  jpeg: fileImageIcon,
  gif: fileImageIcon,
  webp: fileImageIcon,
  bmp: fileImageIcon,
  svg: fileImageIcon
}

const normalizeFileExt = (ext) =>
  String(ext || '')
    .trim()
    .toLowerCase()
    .replace(/^\./, '')

const props = defineProps({
  file: {
    type: Object,
    required: true
  },
  width: {
    type: Number,
    default: 200
  }
})
const emits = defineEmits(['delete'])
const instance = getCurrentInstance()
const failedImageSrc = ref('')
const isUploadSuccess = computed(() => Boolean(props.file?.attachIndex))
const hasDeleteListener = computed(() => Boolean(instance?.vnode.props?.onDelete))
const fileName = computed(
  () => props.file?.filename || props.file?.fileName || props.file?.name || '未命名文件'
)

const cardStyle = computed(() => {
  const width = props.width + 'px'
  return {
    width
  }
})

const fileExt = computed(() => {
  if (props.file?.ext) return normalizeFileExt(props.file.ext)
  const lastDotIndex = fileName.value.lastIndexOf('.')
  return lastDotIndex > -1 ? normalizeFileExt(fileName.value.slice(lastDotIndex + 1)) : ''
})

const fileTypeText = computed(() => {
  if (['md', 'markdown'].includes(fileExt.value)) return 'MARKDOWN'
  return fileExt.value ? fileExt.value.toUpperCase() : '文件'
})

const formatFileSize = (size) => {
  if (!size) return ''
  if (size < 1024) return `${size}B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
  return `${(size / 1024 / 1024).toFixed(1)}MB`
}

const fileMeta = computed(() => {
  const sizeText = formatFileSize(props.file?.size)
  return sizeText ? `${fileTypeText.value} · ${sizeText}` : fileTypeText.value
})

const iconSrc = computed(() => {
  if (
    FILE_ICON_BY_EXTENSION[fileExt.value] === fileImageIcon &&
    props.file?.attachIndex &&
    props.file.attachIndex !== failedImageSrc.value
  ) {
    return props.file.attachIndex
  }
  return FILE_ICON_BY_EXTENSION[fileExt.value] || fileOtherIcon
})
</script>

<style lang="less" scoped>
@font-family: PingFang SC;
@file-card-bg: #ffffff; // paint_6513:129312
@file-card-border: #e5e6eb; // 线条/Line-2 一般线条
@file-card-text: #323233; // 文字/Text-4 强调正文标题
@file-card-subtext: #969799; // 文字/Text-2 次要信息

.uploaded-file-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  width: 200px;
  height: 54px;
  padding: 8px;
  border: 1px solid @file-card-border;
  border-radius: 16px;
  background: @file-card-bg;
  font-family: @font-family;
}

.uploaded-file-card.is-uploading {
  justify-content: center;
}

.uploaded-file-card__uploading {
  display: flex;
  align-items: center;
  gap: 4px;
  color: @file-card-subtext;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
}

.uploaded-file-card__loader {
  font-size: 16px;
  line-height: 16px;
  animation: uploaded-file-loader-spin 1s linear infinite;
}

@keyframes uploaded-file-loader-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.uploaded-file-card__delete {
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 22px;
  height: 22px;
  padding: 2px;
  border: 1px solid @file-card-border;
  border-radius: 64px;
  background: @file-card-bg;
  color: @file-card-text;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.16s ease;

  .icon-close-line {
    font-size: 16px;
  }
}

.uploaded-file-card:hover .uploaded-file-card__delete {
  opacity: 1;
}

.uploaded-file-card__icon {
  flex: none;
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.uploaded-file-card__info {
  flex: 1;
  min-width: 0;
}

.uploaded-file-card__name {
  overflow: hidden;
  color: @file-card-text;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uploaded-file-card__meta {
  margin-top: 2px;
  color: @file-card-subtext;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
}
</style>
